import { body, coverBg, edge, fade, gloss, mix, shade, shadowInk, tint, type ArtPal } from './kit';

/** Roulette: a horizontal strip of square red / dark-slate / one green cells under a centre marker. */
export function rouletteCover(P: ArtPal, p: string): string {
  const cx = 440;
  const cy = 206;
  const size = 80;
  const step = 92;
  const top = P.dark ? mix(P.bg, P.red, 0.07) : tint(P.red, 0.93);
  const mid = P.dark ? mix(P.panel, P.blue, 0.06) : tint(P.blue, 0.94);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, cy]);
  const green = P.teal; // the strip's single ×14 cell (design colour role "green" = teal)
  const pattern: Array<'r' | 'k' | 'g'> = ['k', 'r', 'k', 'g', 'r', 'k', 'r', 'k'];
  const fill = { r: `${p}cr`, k: `${p}ck`, g: `${p}cg` } as const;
  const stroke = { r: edge(P.red, P), k: P.dark ? shade(P.slate, 0.5) : shade(P.slate, 0.35), g: edge(green, P) } as const;

  const cells = pattern
    .map((t, i) => {
      const x = cx + (i - 3) * step - size / 2;
      const y = cy - size / 2;
      return (
        `<rect x="${x}" y="${y}" width="${size}" height="${size}" rx="8" fill="url(#${fill[t]})" stroke="${stroke[t]}" stroke-width="2"/>` +
        `<rect x="${x + 5}" y="${y + size - 18}" width="${size - 10}" height="13" rx="6" fill="#000000" fill-opacity="${t === 'k' ? 0.18 : 0.12}"/>` +
        gloss(x + 22, y + 16, 16, 5, -28, t === 'k' ? 0.2 : 0.42) +
        // centre glyph: soft diamond (no text in art)
        `<path d="M${x + size / 2} ${y + size / 2 - 11}L${x + size / 2 + 11} ${y + size / 2}L${x + size / 2} ${y + size / 2 + 11}L${x + size / 2 - 11} ${y + size / 2}Z" fill="#FFFFFF" fill-opacity="${t === 'k' ? 0.16 : 0.3}"/>`
      );
    })
    .join('');

  const trackC = P.dark ? mix(P.panel, P.ink, 0.06) : '#FFFFFF';
  const markerY0 = cy - size / 2 - 30;
  const markerY1 = cy + size / 2 + 30;

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.16) +
    body(`${p}cr`, P.red, 0.35, 0.35, 0.5, 1) +
    body(`${p}ck`, P.slate, P.dark ? 0.2 : 0.25, 0.35, 0.5, 1) +
    body(`${p}cg`, green, 0.35, 0.35, 0.5, 1) +
    body(`${p}tr`, trackC, 0.3, P.dark ? 0.35 : 0.08, 0.3, 1) +
    body(`${p}mk`, P.orange, 0.4, 0.3, 0.5, 1) +
    `<linearGradient id="${p}fl" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${P.bg}"/><stop offset=".55" stop-color="${P.bg}" stop-opacity=".75"/><stop offset="1" stop-color="${P.bg}" stop-opacity="0"/></linearGradient>` +
    `</defs>`;

  const tri = (y: number, down: boolean) =>
    down
      ? `<path d="M${cx - 17} ${y - 14}Q${cx} ${y - 20} ${cx + 17} ${y - 14}Q${cx + 4} ${y + 8} ${cx} ${y + 10}Q${cx - 4} ${y + 8} ${cx - 17} ${y - 14}Z" fill="url(#${p}mk)" stroke="${edge(P.orange, P)}" stroke-width="2" stroke-linejoin="round"/>`
      : `<path d="M${cx - 17} ${y + 14}Q${cx} ${y + 20} ${cx + 17} ${y + 14}Q${cx + 4} ${y - 8} ${cx} ${y - 10}Q${cx - 4} ${y - 8} ${cx - 17} ${y + 14}Z" fill="url(#${p}mk)" stroke="${edge(P.orange, P)}" stroke-width="2" stroke-linejoin="round"/>`;

  return (
    defs +
    bg.body +
    `<ellipse cx="${cx + 20}" cy="${cy + size / 2 + 34}" rx="300" ry="16" fill="url(#${p}sh)"/>` +
    `<rect x="120" y="${cy - size / 2 - 16}" width="560" height="${size + 32}" rx="14" fill="url(#${p}tr)" stroke="${P.dark ? mix(P.panel, P.ink, 0.14) : shade(P.bg, 0.1)}" stroke-width="2"/>` +
    cells +
    `<rect x="0" y="${cy - size / 2 - 20}" width="330" height="${size + 40}" fill="url(#${p}fl)"/>` +
    // winning cell ring + marker
    `<rect x="${cx - size / 2 - 6}" y="${cy - size / 2 - 6}" width="${size + 12}" height="${size + 12}" rx="12" fill="none" stroke="${P.orange}" stroke-width="3"/>` +
    `<rect x="${cx - 1.5}" y="${markerY0}" width="3" height="${markerY1 - markerY0}" rx="1.5" fill="${P.orange}" fill-opacity=".9"/>` +
    tri(markerY0, true) +
    tri(markerY1, false) +
    gloss(cx - 7, markerY0 - 9, 6, 2.5, -20, 0.55)
  );
}
